import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AudioLines, Loader2, Pause, Play, X } from 'lucide-react';
import api from '../utils/api';

const SAGE = '#7C9A82';
const AMBER = '#D4A574';

function formatSeconds(totalSec) {
  const t = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function statusBadge(status) {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: AMBER };
    case 'transcribed':
      return { label: 'Almost ready', color: AMBER };
    case 'cleaned':
      return { label: 'Cleaning', color: AMBER };
    case 'awaiting_review':
      return { label: 'Review', color: SAGE };
    case 'accepted':
      return { label: 'Saved', color: SAGE };
    case 'empty_speech':
      return { label: 'Empty', color: '#a8a29e' };
    case 'failed':
      return { label: 'Failed', color: AMBER };
    default:
      return { label: status || '—', color: '#a8a29e' };
  }
}

/**
 * Compact voice-note pill (~180×64). Lays out as: square icon tile on the left,
 * title + duration + status on the right, delete X in the top-right corner.
 * Tap the icon tile to play/pause inline; a thin sage line under the text
 * shows progress. No expansion, no waveform display — designed to sit
 * multiple-per-row in a flex-wrap container, mirroring the photo strip
 * pattern but with audio-specific affordances.
 */
export function VoiceNoteCard({ note, onDeleted }) {
  const [phase, setPhase] = useState('idle');
  // idle | loading | playing | paused | error | deleting
  const [progress, setProgress] = useState(0);
  // Fallback duration from the original capture — used when audio.duration
  // is Infinity / NaN (common for MediaRecorder WebM blobs until end-of-stream).
  const fallbackDuration = (note.duration_ms || 0) / 1000;
  const [duration, setDuration] = useState(fallbackDuration);

  const blobUrlRef = useRef(null);
  const audioRef = useRef(null);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        try { a.pause(); } catch { /* ignore */ }
        a.src = '';
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const loadAndPlay = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await api.get(`/api/voice/${note.id}/audio`, {
        responseType: 'blob',
      });
      blobUrlRef.current = URL.createObjectURL(res.data);

      const audio = new Audio(blobUrlRef.current);
      audio.preload = 'auto';

      // The HTMLAudioElement's own events are the source of truth for
      // play/pause state. The Promise returned by play() can resolve before
      // the element actually transitions to playing, leaving React state
      // out of sync — listening for 'play' / 'pause' avoids that.
      audio.addEventListener('play', () => setPhase('playing'));
      audio.addEventListener('pause', () => {
        // 'pause' also fires immediately before 'ended'; skip the spurious one.
        if (audio.ended) return;
        setPhase('paused');
      });
      audio.addEventListener('ended', () => {
        setPhase('idle');
        setProgress(0);
      });
      audio.addEventListener('error', () => setPhase('error'));

      audio.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
        // Otherwise we keep the fallback (note.duration_ms) — common for
        // MediaRecorder WebM where duration stays Infinity until end-of-stream.
      });
      audio.addEventListener('timeupdate', () => {
        const d =
          Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : fallbackDuration || 1;
        const next = Math.min(1, audio.currentTime / d);
        setProgress(Number.isFinite(next) ? next : 0);
      });

      audioRef.current = audio;
      await audio.play(); // 'play' event handler sets phase='playing'
    } catch (err) {
      console.warn('VoiceNoteCard load/play failed:', err?.message);
      setPhase('error');
    }
  }, [note.id, fallbackDuration]);

  const togglePlay = useCallback(() => {
    if (phase === 'idle' || phase === 'error') {
      loadAndPlay();
    } else if (phase === 'playing') {
      audioRef.current?.pause(); // 'pause' event handler sets phase='paused'
    } else if (phase === 'paused') {
      audioRef.current?.play().catch(() => setPhase('error'));
      // 'play' event handler sets phase='playing'
    }
  }, [phase, loadAndPlay]);

  const handleDelete = useCallback(
    async (e) => {
      e.stopPropagation();
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Delete this voice note? The audio will be permanently removed.'
      );
      if (!ok) return;
      setPhase('deleting');
      try {
        if (audioRef.current) {
          try { audioRef.current.pause(); } catch { /* ignore */ }
        }
        await api.delete(`/api/voice/${note.id}`);
        onDeleted?.(note.id);
      } catch (err) {
        console.warn('VoiceNoteCard delete failed:', err?.message);
        setPhase('idle');
      }
    },
    [note.id, onDeleted]
  );

  const badge = useMemo(() => statusBadge(note.status), [note.status]);
  const isPlaying = phase === 'playing';
  const isPaused = phase === 'paused';
  const isLoading = phase === 'loading' || phase === 'deleting';
  const showProgress = isPlaying || isPaused;
  const displayDuration = duration || fallbackDuration;
  const timeLabel = showProgress
    ? `${formatSeconds(displayDuration * progress)} / ${formatSeconds(displayDuration)}`
    : formatSeconds(displayDuration);

  // Default state shows the decorative wave (AudioLines). Playing → Pause.
  // Paused mid-play → Play (so the user knows tapping resumes from position,
  // not restarts). Loading/deleting → spinner.
  let iconEl;
  if (isLoading) {
    iconEl = <Loader2 size={18} className="animate-spin" />;
  } else if (isPlaying) {
    iconEl = <Pause size={18} fill="currentColor" />;
  } else if (isPaused) {
    iconEl = <Play size={18} fill="currentColor" style={{ marginLeft: 2 }} />;
  } else {
    iconEl = <AudioLines size={18} strokeWidth={2} />;
  }

  return (
    <div
      className="group relative w-[180px] rounded-2xl border bg-white/80 dark:bg-stone-800/40 backdrop-blur-sm transition-colors duration-300"
      style={{
        borderColor: isPlaying ? `${SAGE}55` : 'rgba(231,229,228,0.9)',
      }}
    >
      <div className="flex items-center gap-2.5 px-2.5 py-2.5">
        <button
          onClick={togglePlay}
          disabled={isLoading}
          aria-label={
            isPlaying ? 'Pause voice note' : isPaused ? 'Resume voice note' : 'Play voice note'
          }
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-100 bg-white dark:bg-stone-900/40 shadow-sm transition-transform active:scale-95 disabled:opacity-60"
          style={{ color: SAGE }}
        >
          {iconEl}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-stone-800 dark:text-[#F0EDE8] leading-tight truncate">
              Voice Note
            </p>
            {/* Amber pin: this voice note isn't in Chat memory yet. Renders
                nothing once embedding completes. Matches the dot on the
                Voice tab for consistency across surfaces. */}
            {(note.embedding_status === 'pending' ||
              note.embedding_status === 'processing' ||
              note.embedding_status === 'failed') && (
              <span
                role="img"
                aria-label="Embedding pending"
                title={
                  note.embedding_status === 'failed'
                    ? "Couldn't add to Chat memory. Save again to retry."
                    : note.embedding_status === 'processing'
                      ? 'Currently being added to Chat memory…'
                      : 'Not yet used in Chat memory — embedding will generate shortly.'
                }
                className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                style={{ backgroundColor: AMBER }}
              />
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            <span className="tabular-nums">{timeLabel}</span>
            <span className="text-stone-300">•</span>
            <span className="flex items-center gap-1 truncate">
              <span
                className="h-1.5 w-1.5 rounded-full shrink-0"
                style={{ backgroundColor: badge.color }}
              />
              <span className="truncate">{badge.label}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Playback progress underline */}
      {showProgress && (
        <div
          className="absolute left-3 right-3 bottom-1 h-[2px] rounded-full bg-stone-200/70 overflow-hidden"
          aria-hidden="true"
        >
          <div
            className="h-full transition-[width] duration-150 ease-linear"
            style={{ width: `${Math.min(100, progress * 100)}%`, backgroundColor: SAGE }}
          />
        </div>
      )}

      {/* Delete: small, always slightly visible, full opacity on hover/focus */}
      <button
        type="button"
        onClick={handleDelete}
        disabled={isLoading}
        aria-label="Delete voice note"
        className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-white text-stone-500 shadow-sm border border-stone-100 opacity-60 hover:opacity-100 hover:text-rose-500 focus:opacity-100 transition-opacity disabled:opacity-30"
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </div>
  );
}
